import { HeadphonesIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Badge } from "./ui/badge";
import { Card } from "./ui/card";

export type LinkedSupportTicket = {
  id: string;
  fromEmail: string;
  fromName: string | null;
  subject: string;
  status: string;
  priority: string;
  lastMessageAt: string;
  createdAt: string;
};

export function SupportTicketsPanel({
  tickets,
  emptyLabel = "Ingen koblede supporthenvendelser.",
}: {
  tickets: LinkedSupportTicket[];
  emptyLabel?: string;
}) {
  const navigate = useNavigate();

  if (tickets.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-16 text-center">
        <HeadphonesIcon className="h-10 w-10 text-muted-foreground/20 mb-3" />
        <p className="text-sm text-muted-foreground">{emptyLabel}</p>
      </div>
    );
  }

  return (
    <Card className="overflow-hidden">
      <div className="divide-y divide-border">
        {tickets.map((ticket) => (
          <button
            key={ticket.id}
            type="button"
            className="flex w-full items-center gap-4 px-5 py-4 text-left hover:bg-muted/20 transition-colors"
            onClick={() => navigate(`/support/${ticket.id}`)}
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/8 flex-shrink-0">
              <HeadphonesIcon className="h-4 w-4 text-primary" />
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold text-sm truncate">{ticket.subject}</p>
                <StatusBadge status={ticket.status} />
                <PriorityBadge priority={ticket.priority} />
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-xs text-muted-foreground">
                  {ticket.fromName ? `${ticket.fromName} <${ticket.fromEmail}>` : ticket.fromEmail}
                </span>
                <span className="text-xs text-muted-foreground/40">·</span>
                <span className="text-xs text-muted-foreground">{formatDate(ticket.lastMessageAt)}</span>
              </div>
            </div>
          </button>
        ))}
      </div>
    </Card>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; variant: any }> = {
    open: { label: "Åpen", variant: "warning" },
    in_progress: { label: "Under behandling", variant: "secondary" },
    resolved: { label: "Løst", variant: "success" },
    closed: { label: "Lukket", variant: "muted" },
  };
  const { label, variant } = map[status] ?? { label: status, variant: "muted" };
  return <Badge variant={variant}>{label}</Badge>;
}

function PriorityBadge({ priority }: { priority: string }) {
  const map: Record<string, { label: string; variant: any }> = {
    urgent: { label: "Urgent", variant: "destructive" },
    high: { label: "Høy", variant: "destructive" },
    normal: { label: "Normal", variant: "muted" },
    low: { label: "Lav", variant: "muted" },
  };
  const { label, variant } = map[priority] ?? { label: priority, variant: "muted" };
  return <Badge variant={variant}>{label}</Badge>;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("nb-NO", { day: "numeric", month: "short", year: "numeric" });
}
